import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAnalytics } from '@/lib/analytics';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

interface AcceptInvitationResponse {
  success: boolean;
  brand_id?: string;
  brand_name?: string;
  access_level?: string;
  error?: string;
}

/**
 * This component handles invitation acceptance after authentication.
 * It's designed to be a dedicated page that processes invitation tokens
 * passed via URL parameters or localStorage.
 */
export default function ProcessInvitation() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [accountName, setAccountName] = useState<string>('');
  const navigate = useNavigate();
  const { trackEvent, events } = useAnalytics();

  // Get token only from URL parameters
  const invitationToken = searchParams.get('invitation_token') || searchParams.get('token');

  useEffect(() => {
    const processInvitation = async () => {
      console.log('[ProcessInvitation] Starting with token:', invitationToken);
      
      if (!invitationToken) {
        setError('No invitation token found. Please check your invitation link.');
        setLoading(false);
        return;
      }

      try {
        // First, verify if the user is authenticated
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData.session) {
          console.log('[ProcessInvitation] No active session, redirecting to login');
          
          // Redirect to login with invitation token in query params
          navigate(`/login?flow=invite&invitation_token=${invitationToken}`);
          return;
        }

        // Accept the invitation
        console.log('[ProcessInvitation] Accepting invitation with token:', invitationToken);
        const { data, error: inviteError } = await supabase.rpc('accept_brand_invitation', {
          invitation_token: invitationToken
        });

        if (inviteError) {
          console.error('[ProcessInvitation] Error accepting invitation:', inviteError);
          setError(`Failed to accept invitation: ${inviteError.message}`);
          setLoading(false);
          return;
        }

        console.log('[ProcessInvitation] Invitation accepted successfully:', data);
        const responseData = data as unknown as AcceptInvitationResponse;
        
        if (!responseData || !responseData.success) {
          setError(responseData?.error || 'Failed to accept invitation');
          setLoading(false);
          return;
        }
        
        setAccountName(responseData.brand_name || 'the brand');
        
        // Set onboarding as completed
        const { error: updateError } = await supabase
          .schema('marketing_and_customer_success')
          .from('profiles')
          .update({ onboarding_completed: true })
          .eq('id', sessionData.session.user.id);
          
        if (updateError) {
          console.error('[ProcessInvitation] Error updating profile:', updateError);
          // Continue anyway - this isn't critical
        } else {
          console.log('[ProcessInvitation] Set onboarding_completed to true');
        }

        // Token is used, no need to do anything with it now
        
        // Track successful invitation acceptance
        trackEvent(events.USER_LOGGED_IN, {
          user_id: sessionData.session.user.id,
          via_invitation: true,
          invitation_accepted: true
        });
        
        setSuccess(true);
        setLoading(false);
        
        // Wait a moment before redirecting
        setTimeout(() => {
          navigate('/brands');
        }, 2000);
        
      } catch (err) {
        console.error('[ProcessInvitation] Unexpected error:', err);
        setError('An unexpected error occurred. Please try again.');
        setLoading(false);
      }
    };

    processInvitation();
  }, [invitationToken, navigate, trackEvent, events]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-[400px] shadow-md">
          <CardHeader>
            <CardTitle className="text-center">Processing Invitation</CardTitle>
            <CardDescription className="text-center">
              Please wait while we add you to the team...
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center p-6">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-[400px] shadow-md">
          <CardHeader>
            <CardTitle className="text-center text-red-500">Invitation Error</CardTitle>
            <CardDescription className="text-center">
              {error}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4 p-6">
            <XCircle className="h-12 w-12 text-red-500" />
            <Button onClick={() => navigate('/brands')}>
              Go to Brands
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-[400px] shadow-md">
          <CardHeader>
            <CardTitle className="text-center text-green-500">Invitation Accepted</CardTitle>
            <CardDescription className="text-center">
              You have successfully joined {accountName}.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4 p-6">
            <CheckCircle className="h-12 w-12 text-green-500" />
            <p className="text-sm text-center">Redirecting to brands...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}